import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { operationLogApi, OperationLog } from '../../utils/api';
import toast from 'react-hot-toast';
import {
  FileText,
  Download,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  Calendar,
  User,
  Activity,
  Eye,
  X
} from 'lucide-react';

interface LogDetailModalProps {
  log: OperationLog | null;
  onClose: () => void;
}

const LogDetailModal: React.FC<LogDetailModalProps> = ({ log, onClose }) => {
  if (!log) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">操作详情</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500">操作ID</label>
              <p className="text-sm font-medium">{log.id}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">操作时间</label>
              <p className="text-sm font-medium">{new Date(log.created_at).toLocaleString()}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">操作用户</label>
              <p className="text-sm font-medium">{log.username} ({log.user_role})</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">用户ID</label>
              <p className="text-sm font-medium">{log.user_id}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">操作类型</label>
              <p className="text-sm font-medium text-pink-600">{log.action}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">对象类型</label>
              <p className="text-sm font-medium">{log.target_type}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">对象ID</label>
              <p className="text-sm font-medium">{log.target_id || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">对象名称</label>
              <p className="text-sm font-medium">{log.target_name || '-'}</p>
            </div>
          </div>
          
          <div>
            <label className="text-xs text-slate-500">IP地址</label>
            <p className="text-sm font-medium">{log.ip_address}</p>
          </div>
          
          <div>
            <label className="text-xs text-slate-500">操作详情</label>
            <pre className="mt-1 p-3 bg-slate-50 rounded-lg text-xs overflow-x-auto">
              {JSON.stringify(log.details, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminOperationLogs: React.FC = () => {
  const { user } = useAuthStore();
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState<OperationLog | null>(null);
  
  // 筛选条件
  const [filters, setFilters] = useState({
    action: '',
    targetType: '',
    operatorUserId: '',
    startDate: '',
    endDate: '',
  });
  
  // 统计数据
  const [actionStats, setActionStats] = useState<Array<{ action: string; count: number }>>([]);
  const [userStats, setUserStats] = useState<Array<{ user_id: number; username: string; count: number }>>([]);

  const isWebmaster = user?.role === '站长';

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await operationLogApi.getLogs({
        ...filters,
        operatorUserId: filters.operatorUserId ? parseInt(filters.operatorUserId) : undefined,
        page,
        pageSize,
      });
      
      if (response.success) {
        setLogs(response.data);
        setTotal(response.pagination?.total || 0);
      }
    } catch (error) {
      toast.error('加载操作日志失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [actionResponse, userResponse] = await Promise.all([
        operationLogApi.getActionStats(),
        isWebmaster ? operationLogApi.getUserStats() : Promise.resolve({ success: true, data: [] }),
      ]);
      
      if (actionResponse.success) {
        setActionStats(actionResponse.data);
      }
      
      if (userResponse.success) {
        setUserStats(userResponse.data);
      }
    } catch (error) {
      console.error('加载统计数据失败:', error);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [page, pageSize, filters]);

  useEffect(() => {
    loadStats();
  }, []);

  const handleExport = async () => {
    try {
      const blob = await operationLogApi.exportLogs(filters);
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `operation_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('导出成功');
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error('权限不足，只有站长可以导出操作日志');
      } else {
        toast.error('导出失败');
      }
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      action: '',
      targetType: '',
      operatorUserId: '',
      startDate: '',
      endDate: '',
    });
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">操作日志</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isWebmaster ? '查看所有管理员和站长的操作记录' : '仅查看您自己的操作记录'}
          </p>
        </div>
        {isWebmaster && (
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
          >
            <Download className="w-4 h-4" />
            <span>导出日志</span>
          </button>
        )}
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-pink-100 rounded-lg">
              <FileText className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">总操作数</p>
              <p className="text-xl font-bold text-slate-900">{total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Activity className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">操作类型</p>
              <p className="text-xl font-bold text-slate-900">{actionStats.length}</p>
            </div>
          </div>
        </div>
        
        {isWebmaster && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <User className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">操作人员</p>
                <p className="text-xl font-bold text-slate-900">{userStats.length}</p>
              </div>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {userStats.map(stat => (
                <button
                  key={stat.user_id}
                  onClick={() => {
                    handleFilterChange('operatorUserId', stat.user_id.toString());
                  }}
                  className={`w-full text-left px-2 py-1 rounded text-sm flex justify-between items-center hover:bg-slate-50 transition-colors ${
                    filters.operatorUserId === stat.user_id.toString() ? 'bg-orange-50 text-orange-700' : 'text-slate-600'
                  }`}
                >
                  <span>{stat.username}</span>
                  <span className="text-xs opacity-60">{stat.count}次</span>
                </button>
              ))}
              {userStats.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-2">暂无操作记录</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 筛选条件 */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">筛选条件</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">操作类型</label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="">全部</option>
              {actionStats.map(stat => (
                <option key={stat.action} value={stat.action}>{stat.action}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs text-slate-500 mb-1">对象类型</label>
            <select
              value={filters.targetType}
              onChange={(e) => handleFilterChange('targetType', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="">全部</option>
              <option value="user">用户</option>
              <option value="post">帖子</option>
              <option value="category">版块</option>
              <option value="user_group">用户组</option>
              <option value="sensitive_word">敏感词</option>
              <option value="ad">广告</option>
              <option value="site_config">站点配置</option>
            </select>
          </div>
          
          {isWebmaster && (
            <div>
              <label className="block text-xs text-slate-500 mb-1">操作人员</label>
              <select
                value={filters.operatorUserId}
                onChange={(e) => handleFilterChange('operatorUserId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">全部</option>
                {userStats.map(stat => (
                  <option key={stat.user_id} value={stat.user_id}>{stat.username} ({stat.count}次操作)</option>
                ))}
              </select>
            </div>
          )}
          
          <div>
            <label className="block text-xs text-slate-500 mb-1">开始日期</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          
          <div>
            <label className="block text-xs text-slate-500 mb-1">结束日期</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
        </div>
        
        <div className="mt-4 flex items-center justify-end">
          <button
            onClick={resetFilters}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
          >
            重置筛选
          </button>
        </div>
      </div>

      {/* 操作日志列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500">ID</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500">操作用户</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500">操作类型</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500">对象</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500">IP地址</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500">操作时间</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    加载中...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    暂无操作日志
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-600">{log.id}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{log.username}</p>
                          <p className="text-xs text-slate-500">{log.user_role}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                        {log.action}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm text-slate-900">{log.target_type}</p>
                        {log.target_name && (
                          <p className="text-xs text-slate-500">{log.target_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{log.ip_address}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="p-1 text-pink-600 hover:text-pink-800"
                        title="查看详情"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
          <p className="text-sm text-slate-500">
            共 {total} 条记录，第 {page}/{totalPages} 页
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50 text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-slate-600">{page}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-50 text-sm"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 详情弹窗 */}
      {selectedLog && (
        <LogDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
};

export default AdminOperationLogs;
